import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  FitnessCenter as WorkoutIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import { WorkoutApiService } from '../../services/api/WorkoutApiService';
import { WorkoutCount } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface WorkoutStatsCardProps {
  onServiceUnavailable?: () => void;
}

export const WorkoutStatsCard: React.FC<WorkoutStatsCardProps> = ({ onServiceUnavailable }) => {
  const [workoutCount, setWorkoutCount] = useState<WorkoutCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const fetchWorkoutCount = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await WorkoutApiService.getWorkoutCount();
      setWorkoutCount(data);
    } catch (err: any) {
      console.error('Error fetching workout count:', err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('workoutStats.error') || 'Failed to load workout statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkoutCount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchWorkoutCount();
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert 
            severity="error" 
            action={
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!workoutCount) {
    return null;
  }

  const StatItem: React.FC<{ 
    label: string; 
    value: number; 
    icon: React.ReactNode;
    color?: 'primary' | 'secondary' | 'success' | 'info';
  }> = ({ label, value, icon, color = 'primary' }) => (
    <Box textAlign="center" p={2}>
      <Box display="flex" justifyContent="center" mb={1}>
        <Box color={`${color}.main`} fontSize="2rem">
          {icon}
        </Box>
      </Box>
      <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            {t('workoutStats.title') || 'Workout Statistics'}
          </Typography>
          <IconButton size="small" onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
        
        <Grid container spacing={1}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatItem
              label={t('workoutStats.thisWeek') || 'This Week'}
              value={workoutCount.thisWeek}
              icon={<CalendarIcon />}
              color="primary"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatItem
              label={t('workoutStats.thisMonth') || 'This Month'}
              value={workoutCount.thisMonth}
              icon={<CalendarIcon />}
              color="secondary"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatItem
              label={t('workoutStats.last7Days') || 'Last 7 Days'}
              value={workoutCount.last7Days}
              icon={<TrendingIcon />}
              color="success"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatItem
              label={t('workoutStats.last30Days') || 'Last 30 Days'}
              value={workoutCount.last30Days}
              icon={<WorkoutIcon />}
              color="info"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};